import { useState } from 'react';
import {
  Activity,
  Cable,
  Copy,
  Key,
  Plus,
  ShieldCheck,
  Trash2,
  User as UserIcon,
  UserPlus,
} from 'lucide-react';
import { useI18n, useTranslation } from '@solara/i18n';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Dialog,
  Input,
  LanguageToggle,
  Select,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  ThemeToggle,
  useToast,
} from '@solara/ui-shared';
import type { PortalRole, SampleUser } from '@solara/sample-data';
import type { ApiKeyDto, ErpConnectorDto } from '@solara/api-client';
import { useApiKeys, useErpConnectors } from '@solara/api-client/react';
import { ROLE_LABEL, useAuth } from '../auth/AuthContext';

export default function SettingsPage() {
  const { t } = useTranslation('settings');
  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">{t('title')}</h1>
        <p className="text-fg-muted text-sm">{t('subtitle')}</p>
      </header>
      <Tabs defaultValue="profile">
        <TabsList>
          <TabsTrigger value="profile">{t('tabs.profile')}</TabsTrigger>
          <TabsTrigger value="users">{t('tabs.users')}</TabsTrigger>
          <TabsTrigger value="security">{t('tabs.security')}</TabsTrigger>
          <TabsTrigger value="erp">{t('tabs.erp')}</TabsTrigger>
        </TabsList>
        <TabsContent value="profile"><ProfileTab /></TabsContent>
        <TabsContent value="users"><UsersTab /></TabsContent>
        <TabsContent value="security"><SecurityTab /></TabsContent>
        <TabsContent value="erp"><ErpTab /></TabsContent>
      </Tabs>
    </div>
  );
}

// --- Profile -------------------------------------------------------

function ProfileTab() {
  const { t } = useTranslation('settings');
  const { currentUser } = useAuth();
  const { locale } = useI18n();
  const { toast } = useToast();
  const [given, setGiven] = useState(currentUser.givenName);
  const [family, setFamily] = useState(currentUser.familyName);
  const [phone, setPhone] = useState(currentUser.phone);
  const dateFmt = new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' });

  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <Card className="rounded-2xl">
        <CardHeader>
          <CardTitle>{t('profile.personalTitle')}</CardTitle>
        </CardHeader>
        <CardBody className="grid gap-4 sm:grid-cols-2">
          <Input label={t('profile.givenName')} value={given} onChange={(e) => setGiven(e.target.value)} />
          <Input label={t('profile.familyName')} value={family} onChange={(e) => setFamily(e.target.value)} />
          <Input
            label={t('profile.email')}
            value={currentUser.email}
            disabled
            containerClassName="sm:col-span-2"
            dir="ltr"
            hint={t('profile.emailHint')}
          />
          <Input
            label={t('profile.phone')}
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            dir="ltr"
            inputMode="tel"
            containerClassName="sm:col-span-2"
          />
        </CardBody>
        <div className="px-6 pb-6 flex justify-end">
          <Button onClick={() => toast({ title: t('profile.saved'), tone: 'success' })}>{t('profile.save')}</Button>
        </div>
      </Card>
      <Card className="rounded-2xl">
        <CardHeader>
          <CardTitle>{t('profile.preferencesTitle')}</CardTitle>
        </CardHeader>
        <CardBody className="space-y-3 text-sm">
          <Row label={t('profile.language')}><LanguageToggle /></Row>
          <Row label={t('profile.theme')}><ThemeToggle /></Row>
          <Row label={t('profile.locale')}>
            <code className="rounded bg-surface-muted px-2 py-1 text-xs">{locale}</code>
          </Row>
          <Row label={t('profile.role')}>
            <Badge tone="brand">{ROLE_LABEL[currentUser.role]}</Badge>
          </Row>
          <Row label={t('profile.lastLogin')} value={currentUser.lastLoginAt ? dateFmt.format(new Date(currentUser.lastLoginAt)) : '—'} />
        </CardBody>
      </Card>
    </div>
  );
}

// --- Users ---------------------------------------------------------

function UsersTab() {
  const { t } = useTranslation('settings');
  const { users, currentUser, can } = useAuth();
  const { toast } = useToast();
  const [list, setList] = useState<SampleUser[]>(users);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [invite, setInvite] = useState({
    email: '',
    role: 'Accountant' as PortalRole,
  });

  if (!can('settings.users.manage')) {
    return (
      <Card className="rounded-2xl">
        <CardBody className="p-8 text-center text-fg-muted">{t('users.permissionDenied')}</CardBody>
      </Card>
    );
  }

  const counts = list.reduce<Record<PortalRole, number>>(
    (acc, u) => ({ ...acc, [u.role]: (acc[u.role] ?? 0) + 1 }),
    { CustomerAdmin: 0, Accountant: 0, Approver: 0, Viewer: 0 },
  );

  function changeRole(userId: string, role: PortalRole) {
    setList((s) => s.map((u) => (u.userId === userId ? { ...u, role } : u)));
  }
  function suspend(userId: string) {
    setList((s) =>
      s.map((u) => (u.userId === userId ? { ...u, status: u.status === 'suspended' ? 'active' : 'suspended' } : u)),
    );
    toast({ title: t('users.toast.statusUpdated'), tone: 'info' });
  }
  function sendInvite() {
    if (!invite.email) return;
    setList((s) => [
      {
        userId: `new-${Date.now()}`,
        givenName: 'Invited',
        familyName: 'User',
        givenNameAr: 'مستخدم',
        familyNameAr: 'مدعو',
        email: invite.email,
        phone: '+9715xxxxxxxxx',
        role: invite.role,
        status: 'invited',
        mfaEnabled: false,
        lastLoginAt: null,
        createdAt: new Date().toISOString(),
      },
      ...s,
    ]);
    toast({ title: t('users.toast.inviteSent', { email: invite.email }), tone: 'success' });
    setInviteOpen(false);
    setInvite({ email: '', role: 'Accountant' });
  }

  const statusKey = (s: SampleUser['status']) =>
    s === 'active' ? t('users.table.active') : s === 'invited' ? t('users.table.invited') : t('users.table.suspended');

  return (
    <div className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-4">
        {(['CustomerAdmin', 'Accountant', 'Approver', 'Viewer'] as PortalRole[]).map((r) => (
          <Card key={r} className="rounded-2xl">
            <CardBody className="p-4 flex items-center justify-between gap-3">
              <div>
                <div className="text-xs uppercase tracking-wide text-fg-subtle">{t(`users.kpi.${r}`)}</div>
                <div className="text-2xl font-semibold num">{counts[r] ?? 0}</div>
              </div>
              <UserIcon className="h-6 w-6 text-fg-subtle" />
            </CardBody>
          </Card>
        ))}
      </div>

      <div className="flex justify-end">
        <Button onClick={() => setInviteOpen(true)} className="gap-2">
          <UserPlus className="h-4 w-4" />
          {t('users.invite')}
        </Button>
      </div>

      <Card className="rounded-2xl">
        <CardBody>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-fg-subtle">
                  <th className="text-start font-medium pb-2">{t('users.table.user')}</th>
                  <th className="text-start font-medium pb-2">{t('users.table.email')}</th>
                  <th className="text-start font-medium pb-2">{t('users.table.role')}</th>
                  <th className="text-start font-medium pb-2">{t('users.table.status')}</th>
                  <th className="text-start font-medium pb-2">{t('users.table.mfa')}</th>
                  <th className="text-start font-medium pb-2"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {list.map((u) => (
                  <tr key={u.userId}>
                    <td className="py-2.5">
                      <div className="font-medium">{u.givenName} {u.familyName}</div>
                      <div className="text-xs text-fg-subtle" dir="rtl">{u.givenNameAr} {u.familyNameAr}</div>
                    </td>
                    <td className="py-2.5 text-xs" dir="ltr">{u.email}</td>
                    <td className="py-2.5">
                      <Select
                        value={u.role}
                        onChange={(e) => changeRole(u.userId, e.target.value as PortalRole)}
                        disabled={u.userId === currentUser.userId}
                        options={(['CustomerAdmin', 'Accountant', 'Approver', 'Viewer'] as PortalRole[]).map((r) => ({
                          value: r,
                          label: ROLE_LABEL[r],
                        }))}
                        containerClassName="w-44"
                      />
                    </td>
                    <td className="py-2.5">
                      <Badge tone={u.status === 'active' ? 'success' : u.status === 'invited' ? 'warning' : 'neutral'}>
                        {statusKey(u.status)}
                      </Badge>
                    </td>
                    <td className="py-2.5">
                      {u.mfaEnabled ? <Badge tone="success">{t('users.table.mfaOn')}</Badge> : <Badge tone="warning">{t('users.table.mfaOff')}</Badge>}
                    </td>
                    <td className="py-2.5">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => suspend(u.userId)}
                        disabled={u.userId === currentUser.userId}
                      >
                        {u.status === 'suspended' ? t('users.table.reactivate') : t('users.table.suspend')}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardBody>
      </Card>

      <Dialog
        open={inviteOpen}
        onOpenChange={setInviteOpen}
        title={t('users.dialog.title')}
        description={t('users.dialog.subtitle')}
        size="sm"
        footer={
          <>
            <Button variant="ghost" onClick={() => setInviteOpen(false)}>{t('users.dialog.cancel')}</Button>
            <Button onClick={sendInvite}>{t('users.dialog.send')}</Button>
          </>
        }
      >
        <div className="space-y-3">
          <Input
            label={t('users.dialog.email')}
            value={invite.email}
            onChange={(e) => setInvite({ ...invite, email: e.target.value })}
            placeholder={t('users.dialog.emailPlaceholder')}
            type="email"
            dir="ltr"
            required
          />
          <Select
            label={t('users.dialog.role')}
            value={invite.role}
            onChange={(e) => setInvite({ ...invite, role: e.target.value as PortalRole })}
            options={(['CustomerAdmin', 'Accountant', 'Approver', 'Viewer'] as PortalRole[]).map((r) => ({
              value: r,
              label: ROLE_LABEL[r],
            }))}
          />
        </div>
      </Dialog>
    </div>
  );
}

// --- Security ------------------------------------------------------

function SecurityTab() {
  const { t } = useTranslation('settings');
  const { toast } = useToast();
  const apiKeysQuery = useApiKeys();
  const keys: ApiKeyDto[] = apiKeysQuery.data ?? [];
  const [createOpen, setCreateOpen] = useState(false);
  const [createdKey, setCreatedKey] = useState<string | null>(null);
  const [newLabel, setNewLabel] = useState('');

  function createKey() {
    if (!newLabel) return;
    // API-key create endpoint not wired here; show a demo plaintext locally.
    const full =
      'solara_' + Math.random().toString(36).slice(2, 10) + Math.random().toString(36).slice(2, 10);
    setCreatedKey(full);
    setNewLabel('');
    toast({ title: t('security.toast.copied'), tone: 'info' });
  }

  function revoke(_keyId: string) {
    void _keyId;
    toast({ title: t('security.toast.revoked'), tone: 'warning' });
  }

  return (
    <div className="space-y-6">
      <Card className="rounded-2xl">
        <CardHeader>
          <CardTitle>{t('security.mfaTitle')}</CardTitle>
        </CardHeader>
        <CardBody className="space-y-3 text-sm">
          <Row label={t('security.method')} value={<Badge tone="success">TOTP</Badge>} />
          <Row label={t('security.lastVerified')} value={t('security.lastVerifiedValue')} />
          <Row label={t('security.backupCodes')} value={<code className="text-xs">{t('security.backupValue')}</code>} />
          <div className="flex flex-wrap gap-2 pt-1">
            <Button variant="outline" size="sm" className="gap-2">
              <ShieldCheck className="h-4 w-4" />
              {t('security.resetMfa')}
            </Button>
            <Button variant="outline" size="sm" className="gap-2">
              <Activity className="h-4 w-4" />
              {t('security.loginHistory')}
            </Button>
          </div>
        </CardBody>
      </Card>

      <Card className="rounded-2xl">
        <CardHeader>
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <CardTitle>
              {t('security.apiKeysTitle', { count: keys.filter((k) => !k.revokedAt).length })}
            </CardTitle>
            <Button size="sm" onClick={() => setCreateOpen(true)} className="gap-2">
              <Plus className="h-4 w-4" />
              {t('security.newKey')}
            </Button>
          </div>
        </CardHeader>
        <CardBody>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-fg-subtle">
                  <th className="text-start font-medium pb-2">{t('security.table.label')}</th>
                  <th className="text-start font-medium pb-2">{t('security.table.prefix')}</th>
                  <th className="text-start font-medium pb-2">{t('security.table.scopes')}</th>
                  <th className="text-start font-medium pb-2">{t('security.table.lastUsed')}</th>
                  <th className="text-start font-medium pb-2">{t('security.table.status')}</th>
                  <th className="text-start font-medium pb-2"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {keys.map((k) => {
                  const active = !k.revokedAt;
                  return (
                    <tr key={k.id}>
                      <td className="py-2.5">{k.name}</td>
                      <td className="py-2.5 text-xs"><code>{k.prefix}</code></td>
                      <td className="py-2.5">
                        <div className="flex flex-wrap gap-1">
                          {k.scopes.map((s) => <Badge key={s} tone="neutral">{s}</Badge>)}
                        </div>
                      </td>
                      <td className="py-2.5 text-xs">
                        {k.lastUsedAt ? new Date(k.lastUsedAt).toLocaleDateString() : '—'}
                      </td>
                      <td className="py-2.5">
                        <Badge tone={active ? 'success' : 'neutral'}>{active ? 'ACTIVE' : 'REVOKED'}</Badge>
                      </td>
                      <td className="py-2.5">
                        {active ? (
                          <Button size="sm" variant="ghost" onClick={() => revoke(k.id)} className="text-danger">
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        ) : null}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardBody>
      </Card>

      <Dialog
        open={createOpen}
        onOpenChange={(o) => {
          if (!o) {
            setCreateOpen(false);
            setCreatedKey(null);
          }
        }}
        title={createdKey ? t('security.dialog.createdTitle') : t('security.dialog.createTitle')}
        description={createdKey ? t('security.dialog.createdSubtitle') : t('security.dialog.createSubtitle')}
        size="md"
        footer={
          createdKey ? (
            <Button onClick={() => { setCreateOpen(false); setCreatedKey(null); }}>{t('security.dialog.done')}</Button>
          ) : (
            <>
              <Button variant="ghost" onClick={() => setCreateOpen(false)}>{t('security.dialog.cancel')}</Button>
              <Button onClick={createKey}>{t('security.dialog.generate')}</Button>
            </>
          )
        }
      >
        {createdKey ? (
          <div className="space-y-3">
            <div className="rounded-lg border border-border bg-surface-muted px-3 py-2 font-mono text-sm break-all">
              {createdKey}
            </div>
            <Button
              variant="outline"
              size="sm"
              className="gap-2"
              onClick={() => {
                navigator.clipboard.writeText(createdKey);
                toast({ title: t('security.toast.copied'), tone: 'success' });
              }}
            >
              <Copy className="h-4 w-4" />
              {t('security.dialog.copy')}
            </Button>
          </div>
        ) : (
          <Input
            label={t('security.dialog.label')}
            value={newLabel}
            onChange={(e) => setNewLabel(e.target.value)}
            placeholder={t('security.dialog.labelPlaceholder')}
            startIcon={<Key className="h-4 w-4" />}
          />
        )}
      </Dialog>
    </div>
  );
}

// --- ERP -----------------------------------------------------------

function ErpTab() {
  const { t } = useTranslation('settings');
  const { toast } = useToast();
  const erpQuery = useErpConnectors();
  const items: ErpConnectorDto[] = erpQuery.data ?? [];

  return (
    <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
      {items.map((c) => {
        const enabled = c.status === 'active' || c.status === 'healthy';
        return (
          <Card key={c.id} className="rounded-2xl">
            <CardHeader>
              <div className="flex items-center justify-between gap-3">
                <CardTitle>{c.name}</CardTitle>
                <Badge tone={enabled ? 'success' : 'neutral'}>
                  {c.status.toUpperCase()}
                </Badge>
              </div>
            </CardHeader>
            <CardBody className="space-y-2 text-sm">
              <Row label={t('erp.endpoint')} value={<code className="text-[10px]" dir="ltr">{c.baseUrl ?? '—'}</code>} />
              <Row label={t('erp.lastSync')} value={c.lastSyncAt ? new Date(c.lastSyncAt).toLocaleString() : '—'} />
              <div className="flex items-center gap-2 pt-1">
                <Button size="sm" variant="ghost" onClick={() => toast({ title: t('erp.toast.syncTriggered'), tone: 'info' })}>
                  <Cable className="h-4 w-4" />
                  {t('erp.forceSync')}
                </Button>
              </div>
            </CardBody>
          </Card>
        );
      })}
    </div>
  );
}

function Row({ label, value, children }: { label: string; value?: React.ReactNode; children?: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-fg-muted">{label}</span>
      <span className="text-end">{children ?? value}</span>
    </div>
  );
}
